import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  postApiSampleTestsByIdFailMutation,
  postApiSampleTestsByIdReopenMutation,
  postApiSampleTestsByIdSucceedMutation,
  putApiSampleTestsByIdScheduleMutation,
  type Options,
  type PostApiSampleTestsByIdFailData,
  type PostApiSampleTestsByIdFailError,
  type PostApiSampleTestsByIdReopenData,
  type PostApiSampleTestsByIdReopenError,
  type PostApiSampleTestsByIdSucceedData,
  type PostApiSampleTestsByIdSucceedError,
  type PutApiSampleTestsByIdScheduleData,
  type PutApiSampleTestsByIdScheduleError,
  type SampleTestDto,
} from '@/api';

import { isStaleSampleTestError } from '../lib/errors';

/*
 * Запись теста образца: перенос, итог, возврат в тестирование. Кеш - инвалидацией,
 * не оптимистично: переход разрешает сервер (409). Отказ из-за устаревшей строки
 * (409, 404) тоже перезапрашивает список - человек видит, что стало с тестом.
 * Ключ [{ _id: 'getApiSampleTests' }] накрывает все фильтры списка и блок
 * «Узнать результат теста» на «Моих задачах».
 */

const useInvalidateSampleTests = (): (() => Promise<void>) => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({ queryKey: [{ _id: 'getApiSampleTests' }] });
};

// Типы возврата заданы явно (TS2742).

export const useScheduleSampleTest = (): UseMutationResult<
  SampleTestDto,
  PutApiSampleTestsByIdScheduleError,
  Options<PutApiSampleTestsByIdScheduleData>
> => {
  const invalidate = useInvalidateSampleTests();

  return useMutation({
    ...putApiSampleTestsByIdScheduleMutation(),
    onSuccess: invalidate,
    onError: (error) => (isStaleSampleTestError(error) ? invalidate() : null),
  });
};

export const useSucceedSampleTest = (): UseMutationResult<
  SampleTestDto,
  PostApiSampleTestsByIdSucceedError,
  Options<PostApiSampleTestsByIdSucceedData>
> => {
  const invalidate = useInvalidateSampleTests();

  return useMutation({
    ...postApiSampleTestsByIdSucceedMutation(),
    onSuccess: invalidate,
    onError: (error) => (isStaleSampleTestError(error) ? invalidate() : null),
  });
};

export const useFailSampleTest = (): UseMutationResult<
  SampleTestDto,
  PostApiSampleTestsByIdFailError,
  Options<PostApiSampleTestsByIdFailData>
> => {
  const invalidate = useInvalidateSampleTests();

  return useMutation({
    ...postApiSampleTestsByIdFailMutation(),
    onSuccess: invalidate,
    onError: (error) => (isStaleSampleTestError(error) ? invalidate() : null),
  });
};

export const useReopenSampleTest = (): UseMutationResult<
  SampleTestDto,
  PostApiSampleTestsByIdReopenError,
  Options<PostApiSampleTestsByIdReopenData>
> => {
  const invalidate = useInvalidateSampleTests();

  return useMutation({
    ...postApiSampleTestsByIdReopenMutation(),
    onSuccess: invalidate,
    onError: (error) => (isStaleSampleTestError(error) ? invalidate() : null),
  });
};
